/* Editörün "şu an ne yazıyor" bilgisi — varsayılanlar sitenin kendisinden.

   Tema ayarları yalnızca ezmeleri taşıyor (packages/shared/design.ts).
   Editör bir rengi gösterirken boş kutunun yerine bugünkü rengi, bir EN
   alanında bugünkü İngilizceyi göstermeli; o değerler style.css'te ve
   i18n.js'te duruyor ve burada oradan okunuyor. */

import type { Palette } from '@frawgraphy/shared';
import { I18N_JS, STYLE_CSS } from './sources.ts';

let english: Record<string, string> | null = null;

/** i18n.js'in İngilizce sözlüğü — betik sahte bir `window` üzerinde çalıştırılıyor. */
export function englishDefaults(): Record<string, string> {
  if (english) return english;
  const fake: { FRAWGRAPHY_I18N?: { en?: Record<string, string> } } = {};
  try {
    new Function('window', I18N_JS)(fake);
  } catch {
    /* Sözlük okunamazsa EN kutuları boş başlar; editör yine çalışır. */
  }
  english = fake.FRAWGRAPHY_I18N?.en ?? {};
  return english;
}

/** Her rengi dolu palet — style.css'ten okunan varsayılanlar. */
export type FullPalette = Record<keyof Palette, string>;

const VARS: Record<keyof Palette, string> = {
  bg: '--bg',
  bgElev: '--bg-elev',
  fg: '--fg',
  fgMuted: '--fg-muted',
  accent: '--accent'
};

/** style.css'teki koyu ve açık tema renkleri. */
export function paletteDefaults(): { dark: FullPalette; light: FullPalette } {
  const block = (selector: RegExp): string => selector.exec(STYLE_CSS)?.[1] ?? '';
  const read = (css: string, fallback: FullPalette): FullPalette => {
    const out = { ...fallback };
    for (const [key, cssVar] of Object.entries(VARS) as [keyof Palette, string][]) {
      const m = new RegExp(`${cssVar}:\\s*(#[0-9a-fA-F]{6})\\b`).exec(css);
      if (m) out[key] = m[1]!.toLowerCase();
    }
    return out;
  };
  const dark = read(block(/:root\s*\{([^}]*)\}/), { bg: '#060606', bgElev: '#0c0c0c', fg: '#f4f2ef', fgMuted: '#a5a29c', accent: '#c9b28a' });
  /* Açık tema bazı renkleri tanımlamıyorsa koyudakini miras alır. */
  const light = read(block(/:root\[data-theme="light"\]\s*\{([^}]*)\}/), dark);
  return { dark, light };
}

/* ── Kontrast ───────────────────────────────────────────── */

function luminance(hex: string): number {
  const n = Number.parseInt(hex.slice(1), 16);
  const channel = (v: number) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel((n >> 16) & 255) + 0.7152 * channel((n >> 8) & 255) + 0.0722 * channel(n & 255);
}

/** WCAG kontrast oranı, 1–21. */
export function contrast(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}
